import React, { useState, useMemo } from 'react';
import {
  Plus, Search, Phone, Mail, Calendar, ChevronDown, ChevronUp,
  Edit2, Trash2, X, Check, AlertCircle, Clock, TrendingUp,
  Users, FileText, Filter, MoreVertical, Star, Zap, Target,
  PhoneCall, Send, BarChart2
} from 'lucide-react';
import { format, isToday, isBefore, parseISO, differenceInDays } from 'date-fns';
import './App.css';

const STATUS_CONFIG = {
  new:       { label: 'New',        color: '#6366f1', bg: '#eef2ff', icon: Star },
  contacted: { label: 'Contacted',  color: '#0891b2', bg: '#ecfeff', icon: Phone },
  demo_sent: { label: 'Demo Sent',  color: '#7c3aed', bg: '#f5f3ff', icon: FileText },
  follow_up: { label: 'Follow-up',  color: '#d97706', bg: '#fffbeb', icon: Clock },
  closed_won:{ label: 'Closed Won', color: '#059669', bg: '#ecfdf5', icon: Check },
  closed_lost:{ label: 'Closed Lost',color: '#dc2626', bg: '#fef2f2', icon: X },
};

const STATUSES = Object.keys(STATUS_CONFIG);

const INITIAL_LEADS = [
  {
    id: '1', restaurant: 'The Golden Fork', contact: 'Sarah Johnson', email: 'sarah@goldenfork.com',
    phone: '0412 345 678', status: 'follow_up', lastCall: '2026-06-10', demoSent: true,
    demoDate: '2026-06-08', followUpDate: '2026-06-14', notes: 'Very interested, awaiting budget approval.',
    createdAt: '2026-06-01', source: 'Referral',
  },
  {
    id: '2', restaurant: 'Bella Notte', contact: 'Marco Rossi', email: 'marco@bellanotte.com.au',
    phone: '0423 456 789', status: 'demo_sent', lastCall: '2026-06-11', demoSent: true,
    demoDate: '2026-06-11', followUpDate: '2026-06-15', notes: 'Sent full demo. Owner reviewing with manager.',
    createdAt: '2026-06-03', source: 'Cold Outreach',
  },
  {
    id: '3', restaurant: 'Harvest Kitchen', contact: 'Emma Liu', email: 'emma@harvestkitchen.com',
    phone: '0434 567 890', status: 'contacted', lastCall: '2026-06-12', demoSent: false,
    demoDate: '', followUpDate: '2026-06-17', notes: 'Had a 20min intro call. Schedule demo next week.',
    createdAt: '2026-06-05', source: 'Website',
  },
  {
    id: '4', restaurant: 'Spice Route', contact: 'Raj Patel', email: 'raj@spiceroute.com.au',
    phone: '0445 678 901', status: 'new', lastCall: '', demoSent: false,
    demoDate: '', followUpDate: '', notes: 'Referred by The Golden Fork.',
    createdAt: '2026-06-13', source: 'Referral',
  },
  {
    id: '5', restaurant: 'Ocean Blue', contact: 'Chris Tan', email: 'chris@oceanblue.com',
    phone: '0456 789 012', status: 'closed_won', lastCall: '2026-06-09', demoSent: true,
    demoDate: '2026-06-05', followUpDate: '', notes: 'Signed up! Onboarding scheduled for June 20.',
    createdAt: '2026-05-28', source: 'Cold Outreach',
  },
  {
    id: '6', restaurant: 'The Rustic Table', contact: 'Lena Park', email: 'lena@rustictable.com.au',
    phone: '0467 890 123', status: 'new', lastCall: '', demoSent: false,
    demoDate: '', followUpDate: '2026-06-18', notes: 'Met at hospitality expo. Keen on table management.',
    createdAt: '2026-06-14', source: 'Event',
  },
];

const ACTIVITY_LOG = [
  { id: 1, text: <>Added <strong>Spice Route</strong> as a new lead</>, time: '2h ago', color: '#FF6B35' },
  { id: 2, text: <>Sent demo to <strong>Bella Notte</strong></>, time: '5h ago', color: '#7c3aed' },
  { id: 3, text: <><strong>Ocean Blue</strong> marked as <strong>Closed Won</strong> 🎉</>, time: 'Yesterday', color: '#059669' },
  { id: 4, text: <>Called <strong>Harvest Kitchen</strong> — follow-up set</>, time: 'Yesterday', color: '#0891b2' },
  { id: 5, text: <>Added <strong>The Rustic Table</strong> from expo</>, time: '2 days ago', color: '#FF6B35' },
];

const LEAD_SOURCES = ['Cold Outreach', 'Referral', 'Website', 'Event', 'Social Media', 'Other'];

const emptyLead = {
  restaurant: '', contact: '', email: '', phone: '', status: 'new',
  lastCall: '', demoSent: false, demoDate: '', followUpDate: '', notes: '',
  source: 'Cold Outreach',
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  return (
    <span className="status-badge" style={{ color: cfg.color, background: cfg.bg }}>
      {cfg.label}
    </span>
  );
}

function StatsCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="stats-card">
      <div className="stats-icon" style={{ background: color + '18', color }}>
        <Icon size={20} />
      </div>
      <div className="stats-body">
        <div className="stats-value">{value}</div>
        <div className="stats-label">{label}</div>
        {sub && <div className="stats-sub">{sub}</div>}
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function LeadForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = e => {
    e.preventDefault();
    if (!form.restaurant.trim()) return;
    onSave(form);
    onClose();
  };

  return (
    <form className="lead-form" onSubmit={handleSubmit}>
      <div className="form-grid">
        <div className="form-group full">
          <label>Restaurant Name *</label>
          <input required value={form.restaurant} onChange={e => set('restaurant', e.target.value)} placeholder="e.g. The Golden Fork" />
        </div>
        <div className="form-group">
          <label>Contact Person</label>
          <input value={form.contact} onChange={e => set('contact', e.target.value)} placeholder="Full name" />
        </div>
        <div className="form-group">
          <label>Status</label>
          <select value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Email</label>
          <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="contact@restaurant.com" />
        </div>
        <div className="form-group">
          <label>Phone</label>
          <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="04xx xxx xxx" />
        </div>
        <div className="form-group">
          <label>Lead Source</label>
          <select value={form.source} onChange={e => set('source', e.target.value)}>
            {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Last Call Date</label>
          <input type="date" value={form.lastCall} onChange={e => set('lastCall', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Follow-up Date</label>
          <input type="date" value={form.followUpDate} onChange={e => set('followUpDate', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Demo Sent</label>
          <div className="toggle-row">
            <button type="button" className={`toggle ${form.demoSent ? 'on' : ''}`} onClick={() => set('demoSent', !form.demoSent)}>
              <span className="toggle-thumb" />
            </button>
            <span>{form.demoSent ? 'Yes' : 'No'}</span>
          </div>
        </div>
        {form.demoSent && (
          <div className="form-group">
            <label>Demo Date</label>
            <input type="date" value={form.demoDate} onChange={e => set('demoDate', e.target.value)} />
          </div>
        )}
        <div className="form-group full">
          <label>Notes</label>
          <textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any relevant notes..." />
        </div>
      </div>
      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn-primary">Save Lead</button>
      </div>
    </form>
  );
}

function getFollowUpUrgency(dateStr) {
  if (!dateStr) return null;
  const d = parseISO(dateStr);
  if (isToday(d)) return 'today';
  if (isBefore(d, new Date())) return 'overdue';
  if (differenceInDays(d, new Date()) <= 2) return 'soon';
  return null;
}

const LS_KEY = 'dinedesk_leads';

function loadLeads() {
  try {
    const saved = localStorage.getItem(LS_KEY);
    return saved ? JSON.parse(saved) : INITIAL_LEADS;
  } catch {
    return INITIAL_LEADS;
  }
}

export default function App() {
  const [leads, setLeadsRaw] = useState(loadLeads);

  const setLeads = updated => {
    const next = typeof updated === 'function' ? updated(leads) : updated;
    setLeadsRaw(next);
    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch {}
  };
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [view, setView] = useState('table'); // 'table' | 'pipeline'
  const [page, setPage] = useState('leads'); // 'leads' | 'pipeline' | 'reports' | 'myleads' | 'assignments'

  const addLead = form => {
    setLeads(l => [...l, { ...form, id: String(Date.now()), createdAt: new Date().toISOString().slice(0,10) }]);
  };

  const updateLead = form => {
    setLeads(l => l.map(x => x.id === form.id ? form : x));
  };

  const deleteLead = id => {
    setLeads(l => l.filter(x => x.id !== id));
    setDeleteId(null);
  };

  const toggleSort = field => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let res = leads;
    if (statusFilter !== 'all') res = res.filter(l => l.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      res = res.filter(l =>
        l.restaurant.toLowerCase().includes(q) ||
        l.contact.toLowerCase().includes(q) ||
        l.email.toLowerCase().includes(q) ||
        l.notes.toLowerCase().includes(q)
      );
    }
    res = [...res].sort((a, b) => {
      const av = a[sortField] || '';
      const bv = b[sortField] || '';
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return res;
  }, [leads, search, statusFilter, sortField, sortDir]);

  const stats = useMemo(() => ({
    total: leads.length,
    demosOut: leads.filter(l => l.demoSent).length,
    followUpsToday: leads.filter(l => l.followUpDate && isToday(parseISO(l.followUpDate))).length,
    closedWon: leads.filter(l => l.status === 'closed_won').length,
    overdue: leads.filter(l => l.followUpDate && isBefore(parseISO(l.followUpDate), new Date()) && !isToday(parseISO(l.followUpDate))).length,
  }), [leads]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown size={14} style={{ opacity: 0.3 }} />;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  return (
    <div className="app">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-logo">
          <img src="/logo-icon.png" alt="DineDesk" className="sidebar-logo-icon" />
          <span className="sidebar-logo-wordmark">
            <span className="sidebar-logo-dine">Dine</span><span className="sidebar-logo-desk">Desk</span>
          </span>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-section-label">Sales</div>
          <button className={`nav-item${page === 'leads' ? ' active' : ''}`} onClick={() => setPage('leads')}>
            <TrendingUp size={16} />
            <span>Leads Tracker</span>
          </button>
          <button className={`nav-item${page === 'pipeline' ? ' active' : ''}`} onClick={() => setPage('pipeline')}>
            <Target size={16} />
            <span>Pipeline</span>
          </button>
          <button className={`nav-item${page === 'reports' ? ' active' : ''}`} onClick={() => setPage('reports')}>
            <BarChart2 size={16} />
            <span>Reports</span>
          </button>
          <div className="nav-section-label">My Work</div>
          <button className={`nav-item${page === 'myleads' ? ' active' : ''}`} onClick={() => setPage('myleads')}>
            <Users size={16} />
            <span>My Leads</span>
          </button>
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">H</div>
            <div>
              <div className="user-name">Het Shah</div>
              <div className="user-role">Sales Lead</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="main">

        {/* ── LEADS PAGE ── */}
        {page === 'leads' && (<>
          <header className="page-header">
            <div>
              <h1>Restaurant Leads</h1>
              <p className="page-sub">Track every call, demo, and follow-up</p>
            </div>
            <button className="btn-primary" onClick={() => { setEditLead(null); setShowModal(true); }}>
              <Plus size={16} /> Add Lead
            </button>
          </header>

          <div className="win-banner">
            <div className="win-banner-text">
              <strong>This Month's Performance</strong>
              Keep pushing — you're {stats.closedWon} deal{stats.closedWon !== 1 ? 's' : ''} closed this cycle!
            </div>
            <div className="win-rate-pill">
              <div className="win-rate-stat"><div className="val">{stats.total}</div><div className="lbl">Total Leads</div></div>
              <div className="win-rate-stat"><div className="val">{stats.demosOut}</div><div className="lbl">Demos Out</div></div>
              <div className="win-rate-stat"><div className="val">{stats.closedWon}</div><div className="lbl">Won</div></div>
              <div className="win-rate-stat">
                <div className="val">{stats.total > 0 ? Math.round((stats.closedWon / stats.total) * 100) : 0}%</div>
                <div className="lbl">Win Rate</div>
              </div>
            </div>
          </div>

          <div className="stats-row">
            <StatsCard icon={Users} label="Total Leads" value={stats.total} color="#FF6B35" />
            <StatsCard icon={Send} label="Demos Sent" value={stats.demosOut} color="#7c3aed" />
            <StatsCard icon={Clock} label="Follow-ups Today" value={stats.followUpsToday} color="#d97706"
              sub={stats.overdue > 0 ? `${stats.overdue} overdue` : null} />
            <StatsCard icon={Check} label="Closed Won" value={stats.closedWon} color="#059669" />
          </div>

          <div className="quick-actions">
            <button className="quick-action-btn" onClick={() => { setEditLead(null); setShowModal(true); }}><Plus size={14} /> New Lead</button>
            <button className="quick-action-btn"><PhoneCall size={14} /> Log a Call</button>
            <button className="quick-action-btn"><Send size={14} /> Send Demo</button>
            <button className="quick-action-btn"><Zap size={14} /> Follow-ups Due ({stats.followUpsToday + stats.overdue})</button>
          </div>

          <div className="controls-row">
            <div className="search-box">
              <Search size={16} />
              <input placeholder="Search restaurants, contacts, notes..." value={search} onChange={e => setSearch(e.target.value)} />
              {search && <button className="clear-btn" onClick={() => setSearch('')}><X size={14} /></button>}
            </div>
            <div className="filter-group">
              <Filter size={15} />
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="all">All Statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
              </select>
            </div>
            <div className="view-toggle">
              <button className={view === 'table' ? 'active' : ''} onClick={() => setView('table')}>Table</button>
              <button className={view === 'pipeline' ? 'active' : ''} onClick={() => setView('pipeline')}>Pipeline</button>
            </div>
          </div>

          {view === 'table' && (
            <div className="table-card">
              <table className="leads-table">
                <thead>
                  <tr>
                    <th onClick={() => toggleSort('restaurant')}>Restaurant <SortIcon field="restaurant" /></th>
                    <th>Contact</th>
                    <th onClick={() => toggleSort('status')}>Status <SortIcon field="status" /></th>
                    <th onClick={() => toggleSort('lastCall')}>Last Call <SortIcon field="lastCall" /></th>
                    <th>Demo</th>
                    <th onClick={() => toggleSort('followUpDate')}>Follow-up <SortIcon field="followUpDate" /></th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="empty-row">No leads found. Add your first lead!</td></tr>
                  )}
                  {filtered.map(lead => {
                    const urgency = getFollowUpUrgency(lead.followUpDate);
                    return (
                      <tr key={lead.id} className="lead-row">
                        <td className="col-restaurant"><div className="rest-name">{lead.restaurant}</div></td>
                        <td className="col-contact">
                          <div className="contact-name">{lead.contact}</div>
                          {lead.email && <a className="contact-link" href={`mailto:${lead.email}`}><Mail size={11} /> {lead.email}</a>}
                          {lead.phone && <a className="contact-link" href={`tel:${lead.phone}`}><Phone size={11} /> {lead.phone}</a>}
                        </td>
                        <td><StatusBadge status={lead.status} /></td>
                        <td className="col-date">{lead.lastCall ? format(parseISO(lead.lastCall), 'dd MMM yyyy') : <span className="muted">—</span>}</td>
                        <td className="col-demo">
                          {lead.demoSent
                            ? <span className="demo-yes"><Check size={13} /> {lead.demoDate ? format(parseISO(lead.demoDate), 'dd MMM') : 'Sent'}</span>
                            : <span className="demo-no">No</span>}
                        </td>
                        <td className="col-followup">
                          {lead.followUpDate ? (
                            <span className={`followup-date ${urgency || ''}`}>
                              {urgency === 'overdue' && <AlertCircle size={12} />}
                              {urgency === 'today' && <Clock size={12} />}
                              {format(parseISO(lead.followUpDate), 'dd MMM yyyy')}
                            </span>
                          ) : <span className="muted">—</span>}
                        </td>
                        <td className="col-notes"><span className="notes-text">{lead.notes || '—'}</span></td>
                        <td className="col-actions">
                          <button className="icon-btn" title="Edit" onClick={() => { setEditLead(lead); setShowModal(true); }}><Edit2 size={15} /></button>
                          <button className="icon-btn danger" title="Delete" onClick={() => setDeleteId(lead.id)}><Trash2 size={15} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {view === 'pipeline' && (
            <div className="pipeline">
              {STATUSES.map(status => {
                const cfg = STATUS_CONFIG[status];
                const colLeads = filtered.filter(l => l.status === status);
                return (
                  <div key={status} className="pipeline-col">
                    <div className="pipeline-col-header" style={{ borderTopColor: cfg.color }}>
                      <span style={{ color: cfg.color }}>{cfg.label}</span>
                      <span className="pipeline-count">{colLeads.length}</span>
                    </div>
                    <div className="pipeline-cards">
                      {colLeads.map(lead => {
                        const urgency = getFollowUpUrgency(lead.followUpDate);
                        return (
                          <div key={lead.id} className="pipeline-card">
                            <div className="pipeline-card-top">
                              <div className="pc-restaurant">{lead.restaurant}</div>
                              <button className="icon-btn sm" onClick={() => { setEditLead(lead); setShowModal(true); }}><MoreVertical size={14} /></button>
                            </div>
                            {lead.contact && <div className="pc-contact">{lead.contact}</div>}
                            {lead.followUpDate && (
                              <div className={`pc-followup ${urgency || ''}`}><Calendar size={11} />{format(parseISO(lead.followUpDate), 'dd MMM yyyy')}</div>
                            )}
                            {lead.demoSent && <div className="pc-demo"><Check size={11} /> Demo sent</div>}
                            {lead.notes && <div className="pc-notes">{lead.notes}</div>}
                          </div>
                        );
                      })}
                      {colLeads.length === 0 && <div className="pipeline-empty">No leads</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="activity-section">
            <div className="activity-header"><h3>Recent Activity</h3></div>
            <div className="activity-card">
              {ACTIVITY_LOG.map(item => (
                <div key={item.id} className="activity-item">
                  <div className="activity-dot" style={{ background: item.color }} />
                  <div className="activity-text">{item.text}</div>
                  <div className="activity-time">{item.time}</div>
                </div>
              ))}
            </div>
          </div>
        </>)}

        {/* ── PIPELINE PAGE ── */}
        {page === 'pipeline' && (<>
          <header className="page-header">
            <div><h1>Pipeline</h1><p className="page-sub">Full Kanban view of all leads by stage</p></div>
            <button className="btn-primary" onClick={() => { setEditLead(null); setShowModal(true); }}><Plus size={16} /> Add Lead</button>
          </header>
          <div className="pipeline" style={{ marginTop: 8 }}>
            {STATUSES.map(status => {
              const cfg = STATUS_CONFIG[status];
              const colLeads = leads.filter(l => l.status === status);
              return (
                <div key={status} className="pipeline-col">
                  <div className="pipeline-col-header" style={{ borderTopColor: cfg.color }}>
                    <span style={{ color: cfg.color }}>{cfg.label}</span>
                    <span className="pipeline-count">{colLeads.length}</span>
                  </div>
                  <div className="pipeline-cards">
                    {colLeads.map(lead => {
                      const urgency = getFollowUpUrgency(lead.followUpDate);
                      return (
                        <div key={lead.id} className="pipeline-card">
                          <div className="pipeline-card-top">
                            <div className="pc-restaurant">{lead.restaurant}</div>
                            <button className="icon-btn sm" onClick={() => { setEditLead(lead); setShowModal(true); }}><MoreVertical size={14} /></button>
                          </div>
                          {lead.contact && <div className="pc-contact">{lead.contact}</div>}
                          {lead.followUpDate && (
                            <div className={`pc-followup ${urgency || ''}`}><Calendar size={11} />{format(parseISO(lead.followUpDate), 'dd MMM yyyy')}</div>
                          )}
                          {lead.demoSent && <div className="pc-demo"><Check size={11} /> Demo sent</div>}
                          {lead.notes && <div className="pc-notes">{lead.notes}</div>}
                        </div>
                      );
                    })}
                    {colLeads.length === 0 && <div className="pipeline-empty">No leads</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </>)}

        {/* ── REPORTS PAGE ── */}
        {page === 'reports' && (<>
          <header className="page-header">
            <div><h1>Reports</h1><p className="page-sub">Team performance at a glance</p></div>
          </header>
          <div className="win-banner" style={{ marginBottom: 20 }}>
            <div className="win-banner-text">
              <strong>Overall Win Rate</strong>
              {stats.total} leads tracked · {stats.closedWon} closed won
            </div>
            <div className="win-rate-pill">
              <div className="win-rate-stat"><div className="val">{stats.total}</div><div className="lbl">Leads</div></div>
              <div className="win-rate-stat"><div className="val">{stats.demosOut}</div><div className="lbl">Demos</div></div>
              <div className="win-rate-stat"><div className="val">{stats.closedWon}</div><div className="lbl">Won</div></div>
              <div className="win-rate-stat"><div className="val">{leads.filter(l => l.status === 'closed_lost').length}</div><div className="lbl">Lost</div></div>
              <div className="win-rate-stat">
                <div className="val">{stats.total > 0 ? Math.round((stats.closedWon / stats.total) * 100) : 0}%</div>
                <div className="lbl">Win Rate</div>
              </div>
            </div>
          </div>
          <div className="stats-row">
            {STATUSES.map(s => {
              const cfg = STATUS_CONFIG[s];
              const count = leads.filter(l => l.status === s).length;
              return (
                <div key={s} className="stats-card" style={{ borderLeft: `3px solid ${cfg.color}` }}>
                  <div className="stats-icon" style={{ background: cfg.bg, color: cfg.color }}><BarChart2 size={18} /></div>
                  <div className="stats-body">
                    <div className="stats-value" style={{ color: cfg.color }}>{count}</div>
                    <div className="stats-label">{cfg.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>)}

        {/* ── MY LEADS (same as all leads for solo user) ── */}
        {page === 'myleads' && (<>
          <header className="page-header">
            <div><h1>My Leads</h1><p className="page-sub">All your leads</p></div>
            <button className="btn-primary" onClick={() => { setEditLead(null); setShowModal(true); }}><Plus size={16} /> Add Lead</button>
          </header>
          <div className="table-card">
            <table className="leads-table">
              <thead>
                <tr><th onClick={() => toggleSort('restaurant')}>Restaurant <SortIcon field="restaurant" /></th><th>Contact</th><th onClick={() => toggleSort('status')}>Status <SortIcon field="status" /></th><th onClick={() => toggleSort('lastCall')}>Last Call <SortIcon field="lastCall" /></th><th>Demo</th><th onClick={() => toggleSort('followUpDate')}>Follow-up <SortIcon field="followUpDate" /></th><th>Source</th><th></th></tr>
              </thead>
              <tbody>
                {leads.length === 0 && <tr><td colSpan={8} className="empty-row">No leads yet. Add your first!</td></tr>}
                {filtered.map(lead => {
                  const urgency = getFollowUpUrgency(lead.followUpDate);
                  return (
                    <tr key={lead.id} className="lead-row">
                      <td><div className="rest-name">{lead.restaurant}</div></td>
                      <td className="col-contact">
                        <div className="contact-name">{lead.contact}</div>
                        {lead.email && <a className="contact-link" href={`mailto:${lead.email}`}><Mail size={11} /> {lead.email}</a>}
                        {lead.phone && <a className="contact-link" href={`tel:${lead.phone}`}><Phone size={11} /> {lead.phone}</a>}
                      </td>
                      <td><StatusBadge status={lead.status} /></td>
                      <td className="col-date">{lead.lastCall ? format(parseISO(lead.lastCall), 'dd MMM yyyy') : <span className="muted">—</span>}</td>
                      <td>{lead.demoSent ? <span className="demo-yes"><Check size={13} /> Sent</span> : <span className="demo-no">No</span>}</td>
                      <td className="col-followup">
                        {lead.followUpDate ? (
                          <span className={`followup-date ${urgency || ''}`}>
                            {urgency === 'overdue' && <AlertCircle size={12} />}
                            {format(parseISO(lead.followUpDate), 'dd MMM yyyy')}
                          </span>
                        ) : <span className="muted">—</span>}
                      </td>
                      <td><span className="assigned-tag" style={{ background: 'var(--gray-100)', color: 'var(--gray-600)' }}>{lead.source || '—'}</span></td>
                      <td className="col-actions">
                        <button className="icon-btn" onClick={() => { setEditLead(lead); setShowModal(true); }}><Edit2 size={15} /></button>
                        <button className="icon-btn danger" onClick={() => setDeleteId(lead.id)}><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>)}

        {/* ── ASSIGNMENTS → redirect to leads for solo user ── */}
        {page === 'assignments' && (<>
          <header className="page-header">
            <div><h1>Assignments</h1><p className="page-sub">You're the only one — all leads are yours</p></div>
            <button className="btn-primary" onClick={() => setPage('leads')}><TrendingUp size={16} /> Go to Leads</button>
          </header>
          <div className="win-banner">
            <div className="win-banner-text">
              <strong>Solo founder mode 🚀</strong>
              All {stats.total} leads are assigned to you. Head to Leads Tracker to manage them.
            </div>
            <div className="win-rate-pill">
              <div className="win-rate-stat"><div className="val">{stats.total}</div><div className="lbl">Your Leads</div></div>
              <div className="win-rate-stat"><div className="val">{stats.closedWon}</div><div className="lbl">Won</div></div>
              <div className="win-rate-stat"><div className="val">{stats.total > 0 ? Math.round((stats.closedWon / stats.total) * 100) : 0}%</div><div className="lbl">Win Rate</div></div>
            </div>
          </div>
        </>)}

      </main>

      {/* Add/Edit Modal */}
      {showModal && (
        <Modal title={editLead ? 'Edit Lead' : 'Add New Lead'} onClose={() => setShowModal(false)}>
          <LeadForm
            initial={editLead || emptyLead}
            onSave={editLead ? updateLead : addLead}
            onClose={() => setShowModal(false)}
          />
        </Modal>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <Modal title="Delete Lead?" onClose={() => setDeleteId(null)}>
          <div className="delete-confirm">
            <p>This will permanently remove the lead. Are you sure?</p>
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => deleteLead(deleteId)}>Delete</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
